/**
 * Module: tasks API
 * Role: Shared types + fetch helpers for /api/v2/buyer/tasks (BUG-1164)
 * Uses: ./client (apiFetch)
 * Used by: TasksScreen, RecommendationsList
 */
import { apiFetch } from './client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface TaskItem {
  offer_id: string;
  campaign_id: string;
  product_title: string;
  product_image_url: string | null;
  cashback_kopecks: number;
  /** Product price in kopecks — used to render discount badge. */
  product_price_kopecks?: number | null;
  /** FE uses this field as `price_kopecks` in RecommendationsList. */
  price_kopecks?: number | null;
  marketplace: string;
  slots_available: number;
  schedule_date: string;
  /**
   * ADR-040 explicit payment model marker.
   * 'fixed'      — compensation is a fixed kopecks amount (show "+X ₽ вернётся").
   * 'by_receipt' — compensation equals the marketplace receipt (show "До 100% по чеку").
   * null/absent  — legacy slot; treat as 'fixed' for display purposes.
   * SYMMETRIC LIMIT — sync with:
   *   - src/api/v2/buyer/_tasks_schemas.py TaskItem.payment_model
   *   - miniapp/src/screens/TasksScreen.tsx (cashback label branch)
   *   - miniapp/src/screens/ProductScreen.tsx (cashback hero branch)
   *   - miniapp/src/screens/HomeScreen/RecommendationsList.tsx (cashback label branch)
   */
  payment_model?: 'fixed' | 'by_receipt' | null;
}

export interface PaginationMeta {
  total: number;
  limit: number;
  offset: number;
  has_next: boolean;
}

export type GatedReason =
  | 'insufficient_trust'
  | 'slot_limit_reached'
  | 'cooldown_active'
  | 'already_taken'
  | 'creator_required';

export interface GatedTask {
  item: TaskItem;
  reason: GatedReason;
}

export interface TasksResponse {
  items: TaskItem[];
  gated: GatedTask[];
  meta: PaginationMeta;
}

// ─── Fetch ──────────────────────────────────────────────────────────────────

const PAGE_LIMIT = 20;

export async function fetchTasksPage(offset: number): Promise<TasksResponse> {
  return apiFetch<TasksResponse>(
    `/api/v2/buyer/tasks?limit=${PAGE_LIMIT}&offset=${offset}`,
  );
}

// ─── Pagination helpers ─────────────────────────────────────────────────────

/**
 * getNextPageParam for useInfiniteQuery.
 * Returns next offset or undefined (stop).
 */
export function getNextTasksPageParam(
  lastPage: TasksResponse,
): number | undefined {
  if (lastPage.meta.has_next) {
    return lastPage.meta.offset + lastPage.meta.limit;
  }
  return undefined;
}
